import type {
  DiscontinuationDocumentItem,
  DiscontinuationInformationFormValues,
  DiscontinuationReasonOption,
} from "@/components/ActiveProjects/shared/components/DiscontinuationInformationForm";
import { createEmptyDiscontinuationInformationFormValues } from "@/components/ActiveProjects/shared/components/DiscontinuationInformationForm";
import {
  DISCONTINUATION_WORKFLOW_TRANSITION_LABELS,
} from "@/components/ActiveProjects/shared/utils/discontinueProductWorkflow";
import {
  DISCONTINUATION_DATE_FORMAT,
  parseDiscontinuationDateValue,
  parseDiscontinuationReasonSelections,
  serializeDiscontinuationReasonSelections,
} from "@/components/ActiveProjects/shared/utils/discontinuationDateRules";
import {
  getArrayFromJsonString,
  getJsonStringFromArray,
} from "@/components/ActiveProjects/utils/helper";
import { formatToMMDDYYYY } from "@/utils/helper";
import { format, isValid, parse, startOfDay } from "date-fns";

export type NotProgressedDataPayload = {
  notProgressed: boolean;
  notProgressedReason: string;
  notProgressedNote: string;
  notProgressedDate: string;
};

export type DiscontinueDataDocumentRef = {
  id: string | number;
};

export type DiscontinueDataPayload = {
  discontinuationDate: string | null;
  /** API expects JSON-encoded array string, e.g. `"[\"Declining Sales\",\"Diversification\"]"`. */
  discontinuationReason: string | null;
  discontinuationNotes: string | null;
  discontinuationCancelNotes: string | null;
  discontinuationReturnNotes: string | null;
  discontinuationDocuments: DiscontinueDataDocumentRef[];
};

/**
 * Maps cancel-request modal notes to the correct discontinueData field based on the
 * active cancel transition from availableTransitions.
 * - `cancel_to_discontinued_process` → discontinuationCancelNotes
 * - `return_to_discontinued` → discontinuationReturnNotes
 */
export function resolveCancelDiscontinuationNotesForPayload(
  transitionLabel: string | null | undefined,
  cancelNotes: string | null | undefined
): Pick<DiscontinueDataPayload, "discontinuationCancelNotes" | "discontinuationReturnNotes"> {
  const notes = String(cancelNotes ?? "").trim() || null;

  if (
    String(transitionLabel ?? "").trim() ===
    DISCONTINUATION_WORKFLOW_TRANSITION_LABELS.returnToDiscontinued
  ) {
    return {
      discontinuationCancelNotes: null,
      discontinuationReturnNotes: notes,
    };
  }

  return {
    discontinuationCancelNotes: notes,
    discontinuationReturnNotes: null,
  };
}

function serializeDiscontinuationReasonForApi(
  reasonSelections: string[]
): string | null {
  const normalized = reasonSelections
    .map((item) => String(item ?? "").trim())
    .filter(Boolean);
  if (normalized.length === 0) {
    return null;
  }
  return JSON.stringify(normalized);
}

export function formatDiscontinuationDateForApi(dateValue: string): string | null {
  const trimmed = String(dateValue ?? "").trim();
  if (!trimmed) return null;
  const parsed = parseDiscontinuationDateValue(trimmed);
  if (!parsed) return null;
  return `${format(parsed, DISCONTINUATION_DATE_FORMAT)} 12:00:00`;
}

/** Maps discontinuation form values to packaging save `discontinueData`. */
export function mapDiscontinuationFormToDiscontinueData(
  values: DiscontinuationInformationFormValues,
  options?: {
    discontinuationCancelNotes?: string | null;
    discontinuationReturnNotes?: string | null;
  }
): DiscontinueDataPayload {
  const notesTrimmed = String(values.notes ?? "").trim();
  const discontinuationReason = serializeDiscontinuationReasonForApi(
    parseDiscontinuationReasonSelections(values.reasonForDiscontinuation)
  );

  const discontinuationDocuments = (values.documents ?? [])
    .filter((doc) => doc?.id != null && String(doc.id).trim() !== "")
    .map((doc) => ({ id: doc.id as string | number }));

  return {
    discontinuationDate: formatDiscontinuationDateForApi(values.discontinuationDate),
    discontinuationReason,
    discontinuationNotes: notesTrimmed || null,
    discontinuationCancelNotes: options?.discontinuationCancelNotes ?? null,
    discontinuationReturnNotes: options?.discontinuationReturnNotes ?? null,
    discontinuationDocuments,
  };
}

/** Maps Cancel Project modal fields to packaging save `notProgressedData`. */
export function mapCancelProjectFormToNotProgressedData(params: {
  reasonForCanceling: string;
  notesForCanceling: string;
  confirmedAt?: Date;
}): NotProgressedDataPayload {
  const reasonSelections = parseDiscontinuationReasonSelections(params.reasonForCanceling);
  return {
    notProgressed: true,
    notProgressedReason: getJsonStringFromArray(reasonSelections),
    notProgressedNote: String(params.notesForCanceling ?? "").trim(),
    notProgressedDate: formatToMMDDYYYY(params.confirmedAt ?? new Date()),
  };
}

/** Hydrates form `reasonForDiscontinuation` from API `notProgressedReason`. */
export function mapNotProgressedReasonToFormValue(reason: unknown): string {
  return serializeDiscontinuationReasonSelections(getArrayFromJsonString(reason));
}

/** Hydrates form `reasonForDiscontinuation` from API `discontinuationReason`. */
export function mapDiscontinueDataReasonToFormValue(
  reason: string | string[] | null | undefined
): string {
  if (Array.isArray(reason)) {
    return serializeDiscontinuationReasonSelections(
      reason.map((item) => String(item ?? "").trim()).filter(Boolean)
    );
  }

  const trimmed = String(reason ?? "").trim();
  if (!trimmed) {
    return "";
  }

  if (trimmed.startsWith("[")) {
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return serializeDiscontinuationReasonSelections(
          parsed.map((item) => String(item ?? "").trim()).filter(Boolean)
        );
      }
    } catch {
      // Fall through for legacy plain-string values.
    }
  }

  return trimmed;
}

const DISCONTINUATION_DATE_API_FORMAT = `${DISCONTINUATION_DATE_FORMAT} HH:mm:ss`;

/** Listing GET datetime → form date (`MM/dd/yyyy`). */
export function formatDiscontinuationDateForForm(apiValue: unknown): string {
  if (apiValue == null || apiValue === "") {
    return "";
  }
  const trimmed = String(apiValue).trim();
  if (!trimmed) {
    return "";
  }

  for (const fmt of [DISCONTINUATION_DATE_FORMAT, DISCONTINUATION_DATE_API_FORMAT]) {
    const parsed = parse(trimmed, fmt, new Date());
    if (isValid(parsed)) {
      return format(startOfDay(parsed), DISCONTINUATION_DATE_FORMAT);
    }
  }

  const fallback = parseDiscontinuationDateValue(trimmed);
  if (fallback) {
    return format(fallback, DISCONTINUATION_DATE_FORMAT);
  }
  return "";
}

function mapListingDiscontinuationDocuments(raw: unknown): DiscontinuationDocumentItem[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .map((item): DiscontinuationDocumentItem | null => {
      if (item == null || typeof item !== "object") {
        return null;
      }
      const record = item as Record<string, unknown>;
      const id = record.id;
      if (id == null || String(id).trim() === "") {
        return null;
      }
      const nameRaw =
        record.filename ?? record.fileName ?? record.name ?? record.documentTitle ?? null;
      const name =
        nameRaw != null && String(nameRaw).trim() !== "" ? String(nameRaw).trim() : null;
      return { id: id as string | number, name };
    })
    .filter((item): item is DiscontinuationDocumentItem => item != null);
}

function pickDiscontinuationListingFields(
  resp: Record<string, unknown>
): Record<string, unknown> {
  const nested = resp.discontinueData;
  if (nested != null && typeof nested === "object" && !Array.isArray(nested)) {
    return { ...(nested as Record<string, unknown>), ...resp };
  }
  return resp;
}

/** Maps `GET .../discontinue-not-progressed` discontinuation fields into form values. */
export function parseDiscontinuationInformationFromDiscontinueNotProgressed(
  resp: unknown
): DiscontinuationInformationFormValues {
  return parseDiscontinuationInformationFromPackagingListing(resp);
}

/**
 * Maps `GET .../otherLabelingTabListing/{vendorAssessmentId}` discontinuation fields
 * into {@link DiscontinuationInformationFormValues} for the on-page section + modal copy.
 */
export function parseDiscontinuationInformationFromPackagingListing(
  resp: unknown
): DiscontinuationInformationFormValues {
  const empty = createEmptyDiscontinuationInformationFormValues();
  if (resp == null || typeof resp !== "object") {
    return empty;
  }

  const source = pickDiscontinuationListingFields(resp as Record<string, unknown>);
  const discontinuationDateRaw =
    source.discontinuationDate ?? source.discontinuation_date;
  const reasonRaw = source.discontinuationReason ?? source.discontinuation_reason;
  const notesRaw = source.discontinuationNotes ?? source.discontinuation_notes;
  const documentsRaw =
    source.discontinuationDocuments ?? source.discontinuation_documents;

  const discontinuationDate = formatDiscontinuationDateForForm(discontinuationDateRaw);
  const reasonForDiscontinuation = mapDiscontinueDataReasonToFormValue(
    reasonRaw as string | string[] | null | undefined
  );
  const notes = notesRaw != null ? String(notesRaw).trim() : "";
  const documents = mapListingDiscontinuationDocuments(documentsRaw);

  if (
    !discontinuationDate &&
    !reasonForDiscontinuation &&
    !notes &&
    documents.length === 0
  ) {
    return empty;
  }

  return {
    discontinuationDate,
    reasonForDiscontinuation,
    notes,
    documents,
  };
}

/** Reads buyer return notes from packaging listing GET (`discontinueData` or top-level). */
export function parseDiscontinuationReturnNotesFromPackagingListing(
  resp: unknown
): string {
  if (resp == null || typeof resp !== "object") {
    return "";
  }
  const source = pickDiscontinuationListingFields(resp as Record<string, unknown>);
  const raw =
    source.discontinuationReturnNotes ?? source.discontinuation_return_notes;
  return raw != null ? String(raw).trim() : "";
}

function pickNotProgressedListingFields(
  resp: Record<string, unknown>
): Record<string, unknown> {
  const discontinueNotProgressed = resp.discontinueNotProgressed;
  if (
    discontinueNotProgressed != null &&
    typeof discontinueNotProgressed === "object" &&
    !Array.isArray(discontinueNotProgressed)
  ) {
    return { ...(discontinueNotProgressed as Record<string, unknown>), ...resp };
  }
  const nested = resp.notProgressedData;
  if (nested != null && typeof nested === "object" && !Array.isArray(nested)) {
    return { ...(nested as Record<string, unknown>), ...resp };
  }
  return resp;
}

/** Whether listing GET indicates the project was cancelled (`notProgressed`). */
export function parseNotProgressedFromPackagingListing(resp: unknown): boolean {
  if (resp == null || typeof resp !== "object") {
    return false;
  }
  const source = pickNotProgressedListingFields(resp as Record<string, unknown>);
  const raw = source.notProgressed ?? source.not_progressed;
  if (raw === true) {
    return true;
  }
  if (raw === false || raw == null) {
    return false;
  }
  return String(raw).trim().toLowerCase() === "true";
}

/**
 * Maps `GET .../otherLabelingTabListing/{vendorAssessmentId}` cancel-project fields
 * into {@link DiscontinuationInformationFormValues} for the on-page section.
 */
export function parseCancelProjectInformationFromPackagingListing(
  resp: unknown
): DiscontinuationInformationFormValues {
  const empty = createEmptyDiscontinuationInformationFormValues();
  if (resp == null || typeof resp !== "object") {
    return empty;
  }

  const source = pickNotProgressedListingFields(resp as Record<string, unknown>);
  const dateRaw = source.notProgressedDate ?? source.not_progressed_date;
  const reasonRaw = source.notProgressedReason ?? source.not_progressed_reason;
  const notesRaw = source.notProgressedNote ?? source.not_progressed_note;

  const discontinuationDate = formatDiscontinuationDateForForm(dateRaw);
  const reasonForDiscontinuation = mapNotProgressedReasonToFormValue(reasonRaw);
  const notes = notesRaw != null ? String(notesRaw).trim() : "";

  if (!discontinuationDate && !reasonForDiscontinuation && !notes) {
    return empty;
  }

  return {
    discontinuationDate,
    reasonForDiscontinuation,
    notes,
    documents: [],
  };
}

/** Maps `discontinuationReason` from packaging onload API to dropdown options. */
export function mapDiscontinuationReasonOnloadOptions(
  raw: unknown
): DiscontinuationReasonOption[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .map((item): DiscontinuationReasonOption | null => {
      if (item == null || typeof item !== "object") {
        return null;
      }
      const rec = item as Record<string, unknown>;
      const label = String(rec.name ?? rec.id ?? "").trim();
      const value = String(rec.id ?? rec.name ?? "").trim();
      if (!label || !value) {
        return null;
      }
      return { label, value };
    })
    .filter((item): item is DiscontinuationReasonOption => item != null);
}
